/**
 * Dual-track commercial case studies: one visible case at a time, nested
 * gallery Swiper, video lightbox, and a two-stage GSAP entrance. Case nav
 * disables at the ends instead of wrapping, matching the Figma swipe control.
 */
if (!customElements.get('maston-case-studies')) {
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const TOUCH_VIEWPORT_QUERY = '(max-width: 1279px)';
  const SWIPE_DISTANCE = 40;
  const SWIPE_AXIS_RATIO = 1.5;
  const GALLERY_AUTOPLAY_MS = 3000;
  const CSS_EASE = 'power1.out';

  class MastonCaseStudies extends HTMLElement {
    #abortController = null;
    #switchTimeline = null;
    #countTweens = [];
    #swipers = new Map();
    #swiperPromise = null;
    #gsapWaitFrame = 0;
    #isLocked = false;
    #activeCase = null;
    #restoreFocus = null;
    #dialogSource = null;
    #hasCountedEntrance = false;
    #pointerStart = null;

    connectedCallback() {
      if (this.#abortController) return;

      this.stage = this.querySelector('[data-case-stage]');
      this.content = this.querySelector('[data-case-content]');
      this.nav = this.querySelector('[data-case-nav]');
      this.prevControl = this.querySelector('[data-case-previous]');
      this.nextControl = this.querySelector('[data-case-next]');
      this.liveRegion = this.querySelector('[data-case-live]');
      this.dialog = this.querySelector('[data-case-dialog]');
      this.dialogVideo = this.querySelector('[data-case-dialog-video]');
      this.#dialogSource = this.dialogVideo?.querySelector('[data-case-dialog-source]') || null;
      this.cases = Array.from(this.querySelectorAll('[data-case-study]'));

      if (!this.stage || !this.cases.length) return;

      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.#bindEvents(signal);
      this.#activate(this.cases[0], { instant: true, silent: true });
      this.dataset.ready = '';
      this.#mountGalleries();
      this.#bindEntranceCount(signal);
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#abortController = null;
      this.#teardown();
    }

    /**
     * @param {AbortSignal} signal
     */
    #bindEvents(signal) {
      this.querySelector('[data-case-previous]')?.addEventListener(
        'click',
        () => this.#move(-1),
        { signal },
      );
      this.querySelector('[data-case-next]')?.addEventListener(
        'click',
        () => this.#move(1),
        { signal },
      );

      this.content?.addEventListener('pointerdown', this.#onPointerDown, { signal, passive: true });
      this.content?.addEventListener('pointerup', this.#onPointerUp, { signal, passive: true });
      this.content?.addEventListener('pointercancel', this.#onPointerCancel, { signal, passive: true });

      this.addEventListener('click', this.#onClick, { signal });
      this.dialog?.addEventListener('click', this.#onDialogClick, { signal });
      this.dialog?.addEventListener('close', this.#onDialogClose, { signal });
      this.querySelector('[data-case-dialog-close]')?.addEventListener(
        'click',
        () => this.dialog?.close(),
        { signal },
      );

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
      }
    }

    /**
     * @param {MouseEvent} event
     */
    #onClick = (event) => {
      const trigger = event.target instanceof Element
        ? event.target.closest('[data-case-video-open]')
        : null;
      if (!trigger || !this.contains(trigger)) return;
      this.#openVideo(trigger);
    };

    /**
     * @param {MouseEvent} event
     */
    #onDialogClick = (event) => {
      if (event.target === this.dialog) this.dialog.close();
    };

    #onDialogClose = () => {
      this.#stopDialogVideo();
      this.#restoreFocus?.focus();
      this.#restoreFocus = null;
    };

    /**
     * @param {Event} event
     */
    #onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      const blockId = event.detail?.blockId;
      const fromId = blockId
        ? this.querySelector(`[data-case-id="${CSS.escape(blockId)}"]`)
        : null;
      const fromMetric = event.target instanceof Element
        ? event.target.closest('[data-case-study]')
        : null;
      const next = fromId || fromMetric;
      if (next) this.#activate(next, { instant: true });
    };

    /**
     * Pad/mobile: horizontal swipe switches cases. Nested galleries, nav,
     * and other controls keep their own gestures.
     * @param {PointerEvent} event
     */
    #onPointerDown = (event) => {
      if (!this.#isTouchViewport) return;
      if (event.pointerType === 'mouse' || !event.isPrimary) return;
      if (this.#isLocked || this.cases.length < 2 || this.dialog?.open) return;
      if (!(event.target instanceof Element) || !this.contains(event.target)) return;
      if (
        event.target.closest(
          '[data-case-gallery], [data-case-nav], [data-case-video-open], button, a, video, dialog',
        )
      ) {
        return;
      }

      this.#pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      this.content?.setPointerCapture?.(event.pointerId);
    };

    /**
     * @param {PointerEvent} event
     */
    #onPointerUp = (event) => {
      const start = this.#pointerStart;
      this.#pointerStart = null;
      if (!start || start.id !== event.pointerId) return;
      if (!this.#isTouchViewport) return;

      const deltaX = event.clientX - start.x;
      const deltaY = event.clientY - start.y;
      if (Math.abs(deltaX) < SWIPE_DISTANCE) return;
      if (Math.abs(deltaX) < Math.abs(deltaY) * SWIPE_AXIS_RATIO) return;

      const isRtl = getComputedStyle(this).direction === 'rtl';
      const isBackwards = isRtl ? deltaX < 0 : deltaX > 0;
      this.#move(isBackwards ? -1 : 1);
    };

    #onPointerCancel = () => {
      this.#pointerStart = null;
    };

    /**
     * @param {number} direction
     */
    #move(direction) {
      if (this.#isLocked || this.cases.length < 2) return;
      const current = this.cases.indexOf(this.#activeCase);
      const from = current === -1 ? 0 : current;
      const next = from + direction;
      if (next < 0 || next >= this.cases.length) return;
      this.#activate(this.cases[next]);
    }

    /**
     * @param {Element} nextCase
     * @param {{ instant?: boolean, silent?: boolean }} [options]
     */
    #activate(nextCase, { instant = false, silent = false } = {}) {
      if (!nextCase || nextCase === this.#activeCase) {
        this.#syncNav();
        return;
      }

      if (this.#isLocked && !instant) return;

      this.#switchTimeline?.kill();
      this.#switchTimeline = null;

      const previous = this.#activeCase;
      this.#activeCase = nextCase;

      if (instant || this.#prefersReducedMotion || !previous || !window.gsap) {
        this.#isLocked = false;
        this.#applyActiveState();
        this.#syncNav();
        if (!silent && !this.#prefersReducedMotion) this.#countUp(nextCase);
        if (!silent) this.#announce(nextCase);
        return;
      }

      this.#isLocked = true;
      this.#syncNav();
      this.#morph(previous, nextCase);
      if (!silent) this.#announce(nextCase);
    }

    /**
     * @param {Element} outgoing
     * @param {Element} incoming
     */
    #morph(outgoing, incoming) {
      const gsap = window.gsap;
      const outCopy = outgoing.querySelector('[data-case-copy-main]');
      const inCopy = incoming.querySelector('[data-case-copy-main]');
      const outMedia = outgoing.querySelector('[data-case-media-frame]');
      const inMedia = incoming.querySelector('[data-case-media-frame]');

      incoming.removeAttribute('inert');
      incoming.setAttribute('aria-hidden', 'false');
      incoming.dataset.caseActive = 'true';
      outgoing.style.pointerEvents = 'none';

      gsap.set(inCopy, { y: 8, autoAlpha: 0 });
      gsap.set(inMedia, { autoAlpha: 0 });

      this.#switchTimeline = gsap.timeline({
        defaults: { ease: CSS_EASE, overwrite: true },
        onComplete: () => {
          outgoing.dataset.caseActive = 'false';
          outgoing.setAttribute('aria-hidden', 'true');
          outgoing.setAttribute('inert', '');
          outgoing.style.pointerEvents = '';
          gsap.set([outCopy, outMedia, inCopy, inMedia], {
            clearProps: 'transform,opacity,visibility',
          });
          this.#syncNav();
          this.#isLocked = false;
          this.#switchTimeline = null;
        },
      });

      this.#switchTimeline.to(outCopy, { y: -8, autoAlpha: 0, duration: 0.18 }, 0);
      this.#switchTimeline.to(outMedia, { autoAlpha: 0, duration: 0.35 }, 0);
      this.#switchTimeline.fromTo(
        inCopy,
        { y: 8, autoAlpha: 0 },
        { y: 0, autoAlpha: 1, duration: 0.3, immediateRender: false },
        0.24,
      );
      this.#switchTimeline.to(inMedia, { autoAlpha: 1, duration: 0.35 }, 0.18);
      this.#switchTimeline.add(() => this.#countUp(incoming), 0.24);
    }

    #applyActiveState() {
      this.cases.forEach((item) => {
        const isActive = item === this.#activeCase;
        item.dataset.caseActive = isActive ? 'true' : 'false';
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        item.style.pointerEvents = '';
        if (isActive) item.removeAttribute('inert');
        else item.setAttribute('inert', '');
        window.gsap?.set(item.querySelectorAll('[data-case-copy-main], [data-case-media-frame]'), {
          clearProps: 'transform,opacity,visibility',
        });
      });
    }

    #syncNav() {
      const hasMany = this.cases.length > 1;
      const index = this.cases.indexOf(this.#activeCase);
      const from = index === -1 ? 0 : index;

      if (this.nav) this.nav.hidden = !hasMany;
      if (this.prevControl) this.prevControl.disabled = !hasMany || from <= 0;
      if (this.nextControl) this.nextControl.disabled = !hasMany || from >= this.cases.length - 1;
    }

    /**
     * @param {Element} caseEl
     */
    #countUp(caseEl) {
      const gsap = window.gsap;
      if (!gsap || !caseEl || this.#prefersReducedMotion) return;

      this.#killCountTweens();

      caseEl.querySelectorAll('[data-case-count]').forEach((node) => {
        const parsed = this.#parseCount(node.dataset.caseCount || node.textContent);
        if (parsed == null) return;

        const state = { value: 0 };
        const tween = gsap.to(state, {
          value: parsed.value,
          duration: 0.4,
          ease: 'power1.out',
          overwrite: true,
          onUpdate: () => {
            node.textContent = parsed.format(state.value);
          },
          onComplete: () => {
            node.textContent = parsed.format(parsed.value);
          },
        });
        this.#countTweens.push(tween);
      });
    }

    #killCountTweens() {
      this.#countTweens.forEach((tween) => tween.kill());
      this.#countTweens = [];
    }

    /**
     * @param {string} raw
     * @returns {{ value: number, format: (n: number) => string } | null}
     */
    #parseCount(raw) {
      const match = String(raw).trim().match(/-?\d+(?:[.,]\d+)?/);
      if (!match) return null;
      const normalized = match[0].replace(',', '.');
      const value = Number.parseFloat(normalized);
      if (!Number.isFinite(value)) return null;
      const decimals = (normalized.split('.')[1] || '').length;
      return {
        value,
        format: (number) => (decimals > 0 ? number.toFixed(decimals) : String(Math.round(number))),
      };
    }

    /**
     * @param {Element} caseEl
     */
    #announce(caseEl) {
      if (!this.liveRegion) return;
      const title = caseEl.querySelector('[data-case-title]')?.textContent?.trim();
      this.liveRegion.textContent = title || '';
    }

    #mountGalleries() {
      const galleries = this.querySelectorAll('[data-case-gallery]');
      if (!galleries.length) return;
      if (typeof window.loadSwiper !== 'function') {
        console.error('[maston-case-studies] Swiper loader is unavailable.');
        return;
      }

      this.#swiperPromise = window
        .loadSwiper()
        .then((Swiper) => {
          if (!this.isConnected) return;
          this.querySelectorAll('[data-case-gallery]').forEach((gallery) => {
            this.#mountGallery(gallery, Swiper);
          });
        })
        .catch((error) => {
          console.error('[maston-case-studies] Unable to load Swiper.', error);
        })
        .finally(() => {
          this.#swiperPromise = null;
        });
    }

    /**
     * @param {Element} gallery
     * @param {Function} Swiper
     */
    #mountGallery(gallery, Swiper) {
      if (!this.contains(gallery) || gallery.dataset.galleryMounted === 'true') return;

      const viewport = gallery.querySelector('[data-case-gallery-viewport]');
      const track = gallery.querySelector('[data-case-gallery-track]');
      const slides = gallery.querySelectorAll('[data-case-slide]');
      const pagination = gallery.querySelector('[data-case-pagination]');
      const prev = gallery.querySelector('[data-case-gallery-previous]');
      const next = gallery.querySelector('[data-case-gallery-next]');

      if (!viewport || !track || slides.length < 2) return;

      viewport.classList.add('swiper');
      track.classList.add('swiper-wrapper');
      slides.forEach((slide) => slide.classList.add('swiper-slide'));

      try {
        // Reveal every server-rendered slide before Swiper measures the track.
        // Otherwise the progressive-enhancement fallback hides slides 2+, so
        // Swiper initializes as a one-slide gallery and locks pagination/nav.
        gallery.dataset.galleryMounted = 'true';

        const prefersReducedMotion = this.#prefersReducedMotion;
        // Video posters stay put so the Watch trigger never retargets mid-click.
        const isVideoGallery = Array.from(slides).some((slide) => slide.dataset.videoSrc);
        const hasAutoplay = !prefersReducedMotion && !isVideoGallery;
        const setGalleryProgress = (progress) => {
          if (!pagination) return;
          const elapsed = Math.min(1, Math.max(0, progress));
          pagination.style.setProperty('--mcs-gallery-progress', String(elapsed));
        };

        const instance = new Swiper(viewport, {
          effect: 'fade',
          fadeEffect: { crossFade: true },
          speed: prefersReducedMotion ? 0 : 350,
          nested: true,
          rewind: true,
          touchAngle: 45,
          threshold: 8,
          watchOverflow: true,
          a11y: false,
          pagination: pagination ? { el: pagination, clickable: true } : undefined,
          navigation: prev && next ? { prevEl: prev, nextEl: next } : undefined,
          autoplay: hasAutoplay
            ? {
                delay: GALLERY_AUTOPLAY_MS,
                disableOnInteraction: false,
                pauseOnMouseEnter: false,
                stopOnLastSlide: false,
              }
            : false,
          on: {
            slideChange: () => setGalleryProgress(hasAutoplay ? 0 : 1),
            autoplayTimeLeft: (_swiper, _timeLeft, remainingProgress) => {
              setGalleryProgress(1 - remainingProgress);
            },
          },
        });

        this.#swipers.set(gallery, instance);
        setGalleryProgress(hasAutoplay ? 0 : 1);
      } catch (error) {
        gallery.dataset.galleryMounted = 'false';
        viewport.classList.remove('swiper');
        track.classList.remove('swiper-wrapper');
        slides.forEach((slide) => slide.classList.remove('swiper-slide'));
        console.error('[maston-case-studies] Unable to initialize gallery.', error);
      }
    }

    /**
     * @param {Element} trigger
     */
    #openVideo(trigger) {
      const { src, type } = this.#resolveVideo(trigger);
      if (!src || !this.dialog || !this.dialogVideo) return;

      this.#restoreFocus = trigger;
      if (this.#dialogSource) {
        this.#dialogSource.src = src;
        if (type) this.#dialogSource.type = type;
        else this.#dialogSource.removeAttribute('type');
      } else {
        this.dialogVideo.src = src;
      }
      this.dialogVideo.load();
      this.dialog.showModal();
      const playResult = this.dialogVideo.play();
      if (playResult?.catch) playResult.catch(() => {});
    }

    /**
     * A multi-video case plays the poster slide currently shown; before Swiper
     * mounts only the first slide is visible, which the trigger already targets.
     * @param {Element} trigger
     * @returns {{ src: string | undefined, type: string | undefined }}
     */
    #resolveVideo(trigger) {
      const gallery = trigger.closest('[data-case-media-frame]')?.querySelector('[data-case-gallery]');
      const swiper = gallery ? this.#swipers.get(gallery) : null;
      const slide = swiper?.slides?.[swiper.activeIndex];
      if (slide?.dataset.videoSrc) {
        return { src: slide.dataset.videoSrc, type: slide.dataset.videoType };
      }
      return { src: trigger.dataset.videoSrc, type: trigger.dataset.videoType };
    }

    #stopDialogVideo() {
      if (!this.dialogVideo) return;
      this.dialogVideo.pause();
      if (this.#dialogSource) {
        this.#dialogSource.removeAttribute('src');
        this.#dialogSource.removeAttribute('type');
      }
      this.dialogVideo.removeAttribute('src');
      this.dialogVideo.load();
    }

    #bindEntranceCount(signal) {
      const reveal = this.closest('section-entrance-reveal');
      if (!reveal || this.#prefersReducedMotion) return;

      const countActiveCase = () => {
        if (this.#hasCountedEntrance || !reveal.classList.contains('is-visible')) return;
        this.#hasCountedEntrance = true;
        this.#countUp(this.#activeCase);
      };

      reveal.addEventListener('theme-entrance-reveal:change', countActiveCase, { signal });
      countActiveCase();
    }

    get #prefersReducedMotion() {
      return window.matchMedia(REDUCED_MOTION_QUERY).matches;
    }

    get #isTouchViewport() {
      return window.matchMedia(TOUCH_VIEWPORT_QUERY).matches;
    }

    #teardown() {
      window.cancelAnimationFrame(this.#gsapWaitFrame);
      this.#switchTimeline?.kill();
      this.#switchTimeline = null;
      this.#killCountTweens();
      this.#hasCountedEntrance = false;
      this.#isLocked = false;
      this.#pointerStart = null;
      this.#swipers.forEach((swiper, gallery) => {
        swiper.destroy(true, true);
        gallery.dataset.galleryMounted = 'false';
      });
      this.#swipers.clear();
      if (this.dialog?.open) this.dialog.close();
      this.#stopDialogVideo();
      this.#activeCase = null;
    }
  }

  customElements.define('maston-case-studies', MastonCaseStudies);
}
